/**
 * Das Stations-Objektblatt (P6 Block C).
 *
 * Ersetzt die alte Detailseite samt ihrer Fallen: Der Typ ADMIN wird nicht
 * mehr still überschrieben (Altbestand wird als solcher gezeigt), die
 * Stations-Vorschau ist ein benannter Weg (Andreas' ursprünglicher Zweck des
 * Admin-Zugriffs), und die Kopplung hat endlich einen echten Widerruf —
 * das Terminal verliert seinen Zugang sofort, nicht erst über Umwege.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Kennung, Notiz, Zustand } from '../../components/leitstand';

const EINGABE =
  'rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none disabled:opacity-60';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;

interface Station {
  id: string;
  name?: string | null;
  type: 'WSA' | 'VORARBEITER' | 'ADMIN';
  location?: string | null;
  isActive: boolean;
  lastSeenAt?: string | null;
}

interface Kopplung {
  gekoppelt: boolean;
  widerrufen?: string | null;
  seit?: string;
  zuletztGesehen?: string;
  geraet?: string | null;
}

const TYP_LABELS: Record<string, string> = {
  WSA: 'WSA-Station',
  VORARBEITER: 'Vorarbeiter-Station',
  ADMIN: 'Vorschau-Zugang (Altbestand)',
};

function zeit(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function StationBlatt() {
  const { user } = useAuth();
  const { id = '' } = useParams<{ id: string }>();

  const [station, setStation] = useState<Station | null>(null);
  const [form, setForm] = useState({ name: '', location: '', type: 'WSA' });
  const [kopplung, setKopplung] = useState<Kopplung | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');
  const [aktionsFehler, setAktionsFehler] = useState('');
  const [gespeichert, setGespeichert] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [schalterFrage, setSchalterFrage] = useState(false);
  const [widerrufFrage, setWiderrufFrage] = useState(false);

  const [verwendung, setVerwendung] = useState<
    Array<{ text: string; wert: number; ziel?: string }>
  >([]);
  const [verlauf, setVerlauf] = useState<Array<{ wann: string; wer: string; was: string }>>([]);

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const [oRes, kRes, vRes, hRes] = await Promise.all([
        fetch(`/api/einrichten/objekt/stationen/${id}`, { credentials: 'include' }),
        fetch(`/api/einrichten/stationen/${id}/kopplung`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/stationen/${id}/verwendung`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/stationen/${id}/verlauf`, { credentials: 'include' }),
      ]);
      if (!oRes.ok) throw new Error('Die Station konnte nicht geladen werden');
      const o = (await oRes.json()).objekt as Station;
      setStation(o);
      setForm({ name: o.name ?? '', location: o.location ?? '', type: o.type });
      if (kRes.ok) setKopplung(await kRes.json());
      if (vRes.ok) setVerwendung((await vRes.json()).zeilen ?? []);
      if (hRes.ok) setVerlauf((await hRes.json()).zeilen ?? []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Station konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [id]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const api = async (pfad: string, methode: string, body?: unknown) => {
    setAktionsFehler('');
    const res = await fetch(pfad, {
      method: methode,
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const antwort = await res.json().catch(() => null);
    if (!res.ok) throw new Error(antwort?.error || 'Aktion fehlgeschlagen');
    return antwort;
  };

  const melde = (text: string) => {
    setGespeichert(text);
    setTimeout(() => setGespeichert(''), 2600);
  };

  const speichere = async () => {
    setSpeichert(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim() || null,
        location: form.location.trim() || null,
      };
      // Der Alt-Fehler „ADMIN wird still überschrieben" ist damit tot: Der Typ
      // wird nur mitgesendet, wenn er wirklich wählbar war.
      if (station?.type !== 'ADMIN') body.type = form.type;
      await api(`/api/admin/stations/${id}`, 'PATCH', body);
      melde('✓ Gespeichert');
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  const schalteZustand = async (ziel: boolean) => {
    try {
      await api(`/api/einrichten/objekt/stationen/${id}/zustand`, 'POST', { aktiv: ziel });
      setSchalterFrage(false);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Zustand konnte nicht geändert werden');
    }
  };

  const widerrufe = async () => {
    try {
      await api(`/api/einrichten/stationen/${id}/kopplung/widerrufen`, 'POST');
      setWiderrufFrage(false);
      melde('✓ Kopplung widerrufen');
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Widerruf fehlgeschlagen');
    }
  };

  const vorschauPfad = station?.type === 'VORARBEITER' ? '/vorarbeiter' : '/wsa';

  return (
    <LeitstandRahmen
      titel={station?.name || 'Station'}
      benutzer={user?.displayName}
      werkzeuge={
        station ? (
          <span className="flex items-center gap-3">
            <Kennung>{TYP_LABELS[station.type] ?? station.type}</Kennung>
            {station.isActive ? (
              <Zustand art="gut">aktiv</Zustand>
            ) : (
              <span className="font-zahl text-[11px] text-tinte-3">außer Betrieb</span>
            )}
            <button
              onClick={() =>
                station.isActive ? setSchalterFrage(true) : void schalteZustand(true)
              }
              className={KNOPF_LEISE}
            >
              {station.isActive ? 'Außer Betrieb nehmen' : 'Wieder in Betrieb nehmen'}
            </button>
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-6">
        <Link to="/einrichten/stationen" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Stationen
        </Link>

        {schalterFrage && station && (
          <div className="flex flex-wrap items-center gap-3 rounded-leitstand border border-linie-2 bg-flaeche px-4 py-3">
            <span className="text-[13.5px] text-tinte-2">
              Außer Betrieb sperrt das Terminal sofort — Anmeldung und Erfassung sind dort dann
              nicht mehr möglich. Alles bereits Erfasste bleibt unverändert.
            </span>
            <button onClick={() => void schalteZustand(false)} className={KNOPF_PRIMAER}>
              Außer Betrieb nehmen
            </button>
            <button onClick={() => setSchalterFrage(false)} className={KNOPF_LEISE}>
              Abbrechen
            </button>
          </div>
        )}

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Station …</p>
        )}

        {!laden && !fehler && station && (
          <>
            <Karte titel="Stammdaten" marke="ein Speichern, eine Rückmeldung">
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Name
                    </span>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={`${EINGABE} w-full`}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Standort
                    </span>
                    <input
                      value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                      className={`${EINGABE} w-full`}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Typ
                    </span>
                    {station.type === 'ADMIN' ? (
                      <>
                        <input value={TYP_LABELS.ADMIN} disabled className={`${EINGABE} w-full`} />
                        <span className="text-xs text-tinte-3">
                          Alter Vorschau-Zugang — die Vorschau braucht heute keine eigene Station
                          mehr; dieser Eintrag entfällt beim Cutover (Z14).
                        </span>
                      </>
                    ) : (
                      <select
                        value={form.type}
                        onChange={(e) => setForm({ ...form, type: e.target.value })}
                        className={`${EINGABE} w-full`}
                      >
                        <option value="WSA">WSA-Station (Waschschleuderautomaten)</option>
                        <option value="VORARBEITER">Vorarbeiter-Station</option>
                      </select>
                    )}
                  </label>
                </div>
                {aktionsFehler && <p className="text-[13px] text-stoer">{aktionsFehler}</p>}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => void speichere()}
                    disabled={speichert}
                    className={KNOPF_PRIMAER}
                  >
                    Speichern
                  </button>
                  {gespeichert && (
                    <span className="font-zahl text-[13px] text-lauf">{gespeichert}</span>
                  )}
                </div>
              </div>
            </Karte>

            <Karte titel="Kopplung" marke="das Terminal hinter dieser Station">
              <div className="flex flex-col gap-3">
                {!kopplung || (!kopplung.gekoppelt && !kopplung.widerrufen) ? (
                  <p className="text-sm text-tinte-2">
                    Keine Kopplung bekannt. Ein neues Terminal wird über{' '}
                    <Link to="/einrichten/geraet-koppeln" className="text-akzent hover:underline">
                      Gerät koppeln
                    </Link>{' '}
                    eingerichtet.
                  </p>
                ) : (
                  <>
                    <div className="grid gap-3 text-sm [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
                      <div>
                        <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3 block">
                          Zustand
                        </span>
                        {kopplung.gekoppelt ? (
                          <Zustand art="gut">gekoppelt</Zustand>
                        ) : (
                          <span className="text-stoer">
                            widerrufen am {zeit(kopplung.widerrufen)}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3 block">
                          Gekoppelt seit
                        </span>
                        <span className="font-zahl">{zeit(kopplung.seit)}</span>
                      </div>
                      <div>
                        <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3 block">
                          Zuletzt gesehen
                        </span>
                        <span className="font-zahl">{zeit(kopplung.zuletztGesehen)}</span>
                      </div>
                    </div>
                    {kopplung.geraet && (
                      <p className="text-xs text-tinte-3 break-all">Gerät: {kopplung.geraet}</p>
                    )}
                    {kopplung.gekoppelt && (
                      <div className="flex flex-wrap items-center gap-2">
                        {!widerrufFrage ? (
                          <button onClick={() => setWiderrufFrage(true)} className={KNOPF_LEISE}>
                            Kopplung widerrufen
                          </button>
                        ) : (
                          <>
                            <span className="text-[13px] text-tinte-2">
                              Das Terminal verliert seinen Zugang sofort und muss neu gekoppelt
                              werden.
                            </span>
                            <button onClick={() => void widerrufe()} className={KNOPF_PRIMAER}>
                              Ja, widerrufen
                            </button>
                            <button onClick={() => setWiderrufFrage(false)} className={KNOPF_LEISE}>
                              Abbrechen
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </Karte>

            {station.type !== 'ADMIN' && (
              <Karte titel="Stations-Vorschau" marke="sehen, was die Wäscher sehen">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm text-tinte-2">
                    Öffnet die Erfassung dieser Stationsart im Admin-Modus — Sichtprüfung aus dem
                    Büro, ohne in die Produktion zu gehen. Test-Einträge bleiben für die Stationen
                    unsichtbar.
                  </p>
                  <a href={vorschauPfad} target="_blank" rel="noreferrer" className={KNOPF_LEISE}>
                    Station ansehen ↗
                  </a>
                </div>
              </Karte>
            )}

            <Karte titel="Verwendung" marke="Wirkung sichtbar, bevor man handelt">
              {verwendung.length === 0 ? (
                <p className="py-4 text-center text-sm text-tinte-3">
                  Noch nichts von dieser Station erfasst.
                </p>
              ) : (
                <div className="flex flex-col">
                  {verwendung.map((v, i) => {
                    const inhalt = (
                      <>
                        <span className="font-zahl text-[15px] font-medium">
                          {v.wert.toLocaleString('de-DE')}
                        </span>
                        <span className="text-sm">{v.text}</span>
                        {v.ziel && <span className="ml-auto font-zahl text-tinte-3">→</span>}
                      </>
                    );
                    const klasse = `flex items-center gap-3 px-1 py-3 ${i < verwendung.length - 1 ? 'border-b border-linie' : ''}`;
                    return v.ziel ? (
                      <Link key={v.text} to={v.ziel} className={`${klasse} hover:bg-flaeche-2`}>
                        {inhalt}
                      </Link>
                    ) : (
                      <div key={v.text} className={klasse}>
                        {inhalt}
                      </div>
                    );
                  })}
                </div>
              )}
            </Karte>

            <Karte titel="Verlauf" marke="aus dem Protokoll, neueste zuerst">
              {verlauf.length === 0 ? (
                <p className="py-4 text-center text-sm text-tinte-3">
                  Noch keine Einträge im Protokoll.
                </p>
              ) : (
                <div className="flex flex-col">
                  {verlauf.map((z, i) => (
                    <div
                      key={`${z.wann}-${i}`}
                      className={`grid grid-cols-[110px_130px_1fr] gap-3 px-1 py-2.5 text-[13px] max-sm:grid-cols-[90px_1fr] ${
                        i < verlauf.length - 1 ? 'border-b border-linie' : ''
                      }`}
                    >
                      <span className="font-zahl text-xs text-tinte-3">
                        {new Date(z.wann).toLocaleString('de-DE', {
                          timeZone: 'Europe/Berlin',
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      <span className="text-tinte-2">{z.wer}</span>
                      <span className="max-sm:col-span-2">{z.was}</span>
                    </div>
                  ))}
                </div>
              )}
            </Karte>
          </>
        )}
      </div>
    </LeitstandRahmen>
  );
}
